
import React from 'react';
import { motion } from 'framer-motion';
import { Star, MapPin, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import DiscountBadge from './DiscountBadge';
import { BusinessBadges } from './BusinessBadges';
import OpeningHoursText from './OpeningHoursText';
import { useCommunity } from '@/contexts/CommunityContext';
import { Badge } from '@/components/ui/badge';
import { PATH } from '@/routes/routeConfig';
import { useTranslation } from 'react-i18next';
import { WeeklyHours } from '@communitly/shared';

export interface ServiceCardProps {
  id: string;
  slug?: string;
  name: string;
  imageUrl: string;
  rating?: number;
  reviewCount?: number;
  location: string;
  distance?: string;
  delay?: number;
  description?: string;
  featured?: boolean;
  is_active?: boolean;
  is_listed?: boolean;
  opening_hours?: WeeklyHours | null;
  discount?: {
    id: string;
    amount: number;
    isPercentage: boolean;
    appliesToAll: boolean;
    type?: 'Paid' | 'Free';
    description?: string | null;
  } | null;
  isMobile?: boolean;
  layout?: 'grid' | 'row';
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  id,
  slug,
  name,
  imageUrl,
  rating,
  reviewCount = 0,
  location,
  distance,
  delay = 0,
  is_active,
  opening_hours,
  discount,
  isMobile = false,
  layout = 'grid'
}) => {
  const { getParameter } = useCommunity();
  const currency = getParameter<string>('currency') || '$';
  const { t } = useTranslation();
  const displayTitle = name || t('service.card.unnamed');
  const isPending = is_active === false;

  if (layout === 'row') {
    // Row layout for infinite scroll
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay }}
        className="group service-card"
      >
        <Link to={PATH.SERVICE_VIEW.replace(':slug', slug)}>
          <div className="rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-lg border border-gray-100 transition-all duration-300 h-full">
            <div className={`${isMobile ? 'flex flex-col' : 'flex flex-row'}`}>
              {/* Image Section */}
              <div className={`service-image relative ${isMobile ? 'aspect-[4/2.5] w-full' : 'w-48 flex-shrink-0 aspect-[4/3]'}`}>
                <img
                  src={imageUrl}
                  alt={displayTitle}
                  className="w-full h-full object-cover"
                />
                
                {/* Discount Badge - Top Left */}
                {discount && (
                  <div className="absolute top-3 left-3">
                    <DiscountBadge 
                      amount={discount.amount} 
                      isPercentage={discount.isPercentage} 
                      appliesToAll={discount.appliesToAll}
                      type={discount.type || 'Paid'}
                      size="sm"
                      hideLabel={true}
                    />
                  </div>
                )}

                {/* Pending Status Badge - Top Left (fallback if no discount) */}
                {isPending && !discount && (
                  <div className="absolute top-3 left-3">
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 flex items-center gap-1.5 px-2 py-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">{t('service.card.pending')}</span>
                    </Badge>
                  </div>
                )}

                
                <div className="absolute inset-0 bg-gradient-to-b from-black/0 to-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              {/* Content Section */}
              <div className="flex-1 p-4 md:p-5 relative">
                <h3 className="font-semibold text-lg md:text-xl mb-2 line-clamp-1 transition-colors">{displayTitle}</h3>
                
                {/* Rating and Location Combined */}
                <div className="flex items-center justify-between text-sm mb-3">
                  <div className="flex items-center text-muted-foreground">
                    <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span className="truncate">{location}</span>
                    {distance && (
                      <span className="ml-2 text-sm">· {distance}</span>
                    )}
                  </div>
                  <div className="flex items-center flex-shrink-0">
                    {reviewCount > 0 && rating !== undefined ? (
                      <>
                        <Star className="w-4 h-4 text-yellow-500 mr-1" fill="currentColor" />
                        <span className="text-sm font-medium">{rating.toFixed(1)}</span>
                        <span className="text-sm text-muted-foreground ml-1">({reviewCount})</span>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">{t('service.card.no_reviews')}</span>
                    )}
                  </div>
                </div>
                
                {/* Opening Hours Text */}
                <div className="mb-3">
                  <OpeningHoursText 
                    openingHours={opening_hours}
                    size="sm"
                    showIcon={false}
                  />
                </div>
                
                {/* Business Achievement Badges */}
                <BusinessBadges 
                  businessId={id}
                  mode="compact"
                  maxBadges={3}
                  showSkeletonOnLoad={false}
                  className="min-h-0"
                />
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  // Grid layout (default)
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="group service-card"
    >
      <Link to={PATH.SERVICE_VIEW.replace(':slug', slug)}>
        <div className="rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-lg border border-gray-100 transition-all duration-300 h-full">
          <div className={`service-image relative ${isMobile ? 'aspect-[4/2.5]' : 'aspect-[4/2.5]'}`}>
            <img
              src={imageUrl}
              alt={displayTitle}
              className="w-full h-full object-cover"
            />
            
            {/* Discount Badge - Top Left */}
            {discount && (
              <div className="absolute top-3 left-3">
                <DiscountBadge 
                  amount={discount.amount} 
                  isPercentage={discount.isPercentage} 
                  appliesToAll={discount.appliesToAll}
                  type={discount.type || 'Paid'}
                  size="sm"
                  hideLabel={true}
                />
              </div>
            )}

            {/* Pending Status Badge - Top Left (fallback if no discount) */}
            {isPending && !discount && (
              <div className="absolute top-3 left-3">
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 flex items-center gap-1.5 px-2 py-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">{t('service.card.pending')}</span>
                </Badge>
              </div>
            )}

            
            <div className="absolute inset-0 bg-gradient-to-b from-black/0 to-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <div className="p-3.5 relative">
            <h3 className="font-semibold text-base mb-1.5 line-clamp-1 transition-colors">{displayTitle}</h3>
            
            {/* Rating and Location Combined */}
            <div className="flex items-center justify-between text-xs mb-2.5">
              <div className="flex items-center text-muted-foreground">
                <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{location}</span>
                {distance && (
                  <span className="ml-1 text-xs">· {distance}</span>
                )}
              </div>
              <div className="flex items-center flex-shrink-0">
                {reviewCount > 0 && rating !== undefined ? (
                  <>
                    <Star className="w-3.5 h-3.5 text-yellow-500 mr-1" fill="currentColor" />
                    <span className="text-xs font-medium">{rating.toFixed(1)}</span>
                    <span className="text-xs text-muted-foreground ml-1">({reviewCount})</span>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">{t('service.card.no_reviews')}</span>
                )}
              </div>
            </div>
            
            {/* Opening Hours Text */}
            <div className="mb-2.5">
              <OpeningHoursText 
                openingHours={opening_hours}
                size="sm"
                showIcon={false}
              />
            </div>
            
            {/* Business Achievement Badges */}
            <BusinessBadges 
              businessId={id}
              mode="compact"
              maxBadges={2}
              showSkeletonOnLoad={false}
              className="min-h-0"
            />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ServiceCard;
